import { useMutation, useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { memo, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import Button from 'shopper/components/Button';
import Heading from 'shopper/components/Heading';
import Icon from 'shopper/components/Icon';
import Spinner from 'shopper/components/Spinner';
import Switch from 'shopper/components/Switch';
import Text from 'shopper/components/Text';

import { LGPD_ALERT_DIALOG_KEY } from 'components/LGPDAlertDialog';

import useScrollControl from 'hooks/useScrollControl';
import useToast from 'hooks/useToast';

import { getLocalStoredConsents, saveLocalConsents } from 'lib/lgpd';
import { setLocalStorage } from 'lib/localStorage';
import placeholder from 'lib/placeholder';
import { consentQueryKeys } from 'lib/queryKeys';

import { useUser } from 'providers/UserProvider';

import {
  getLGPDConsentsList,
  getLGPDUserConsentsList,
  postLGPDConsents,
} from 'services/user';

import LOCAL_STORAGE from 'constants/localStorage';

const ESSENTIAL_COOKIES_ID = 5;
const SORT_POSITIONS_BY_CONSENT_IDS = { 5: 1, 1: 2, 2: 3, 3: 4 };

const ConsentsList = ({ className }) => {
  const { showToast } = useToast();
  const { closeScrollable } = useScrollControl();
  const { isLoggedIn, loadingLoggedInUser } = useUser();
  const [currentConsents, setCurrentConsents] = useState([]);

  const {
    data: consents,
    isInitialLoading,
    isLoading,
    isSuccess,
  } = useQuery({
    queryKey: consentQueryKeys.list(),
    queryFn: ({ signal }) =>
      isLoggedIn
        ? getLGPDUserConsentsList({ signal })
        : getLGPDConsentsList({ signal }),
    select: (data) => data?.consents,
    enabled: !loadingLoggedInUser,
  });

  const { mutate: mutateSavePreferences } = useMutation({
    mutationFn: (newConsents) => postLGPDConsents({ consents: newConsents }),
    onError: () => {
      showToast({
        iconLeft: <Icon name="info-sign" />,
        text: placeholder('toasts.errors.unknown'),
      });
    },
    onSuccess: () => {
      showToast({
        text: placeholder('toasts.successes.consents.saved'),
        type: 'success',
      });
    },
  });

  useEffect(() => {
    if (isSuccess && !loadingLoggedInUser) {
      if (isLoggedIn) {
        setCurrentConsents(consents);
        return;
      }

      setCurrentConsents(
        consents.map((consent) => ({
          ...consent,
          consentStatus: getLocalStoredConsents().find(
            ({ consentId }) => consentId === consent.consentId
          )?.consentStatus,
        }))
      );
    }
  }, [
    isSuccess,
    consents,
    isLoggedIn,
    loadingLoggedInUser,
    setCurrentConsents,
  ]);

  const onSwitchClick = (consentId) => {
    setCurrentConsents((prevState) =>
      prevState.map((consent) => {
        if (consent.consentId === consentId) {
          return {
            ...consent,
            consentStatus: consent.consentStatus === 1 ? 0 : 1,
          };
        }

        return consent;
      })
    );
  };

  const onSavePreferences = () => {
    const consentsWithIdAndStatusWithoutEssentialCookies = currentConsents
      .map(({ consentId, consentStatus }) => ({
        consentId,
        consentStatus,
      }))
      .filter(({ consentId }) => consentId !== ESSENTIAL_COOKIES_ID);

    if (isLoggedIn) {
      mutateSavePreferences(consentsWithIdAndStatusWithoutEssentialCookies);
    } else {
      /**
       * Saves the consents in local storage for unlogged users
       */
      saveLocalConsents(consentsWithIdAndStatusWithoutEssentialCookies);
    }

    // Ensures to close LGPD dialog
    closeScrollable(LGPD_ALERT_DIALOG_KEY);
    setLocalStorage(LOCAL_STORAGE.IS_SHOWING_LGPD_ALERT, false);
    showToast({
      text: placeholder('toasts.successes.consents.saved'),
      type: 'success',
    });
  };

  if (isLoading || isInitialLoading) {
    return (
      <div className="flex h-80 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className={twMerge('flex w-full flex-col', className)}>
      {consents
        .sort(
          (a, b) =>
            SORT_POSITIONS_BY_CONSENT_IDS[a.consentId] -
            SORT_POSITIONS_BY_CONSENT_IDS[b.consentId]
        )
        .map(({ consentId, consentName, consentDescription }, index) => (
          <div
            key={consentId}
            className="flex items-center justify-between border-t border-neutral-high-500 py-4 md:py-6"
          >
            <div>
              <Heading className="mb-4" size="size3">
                {consentName}
              </Heading>
              <Text className="pr-6 md:pr-14">{consentDescription}</Text>
            </div>
            <Switch
              checked={
                consentId === ESSENTIAL_COOKIES_ID
                  ? true
                  : Boolean(currentConsents[index]?.consentStatus)
              }
              disabled={consentId === ESSENTIAL_COOKIES_ID}
              value={currentConsents[index]?.consentStatus}
              onClick={() => onSwitchClick(consentId)}
            />
          </div>
        ))}
      <Button
        className="mt-4 w-full self-end md:mt-6 md:w-fit"
        htmlType="submit"
        onClick={onSavePreferences}
      >
        {placeholder('actions.savePreferences')}
      </Button>
    </div>
  );
};

ConsentsList.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

export default memo(ConsentsList);
